/**
 * Price type class
 *
 * Mirrors `Hops\Types\Price` on the backend.
 *
 * Values are pence inc. VAT.
 *
 * @package HOPS
 * @subpackage Types
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
class Price {

	/**
	 * Construct an instance.
	 *
	 * @param {Integer} Value
	 * @param {Number} VatProportion 0-100
	 * @param {Number} VatRate 0-100
	 * @return {self}
	 */
	constructor(
		Value,
		VatProportion,
		VatRate
	) {

		/**
		 * Price value
		 *
		 * @type {Integer} Pence, inc. VAT
		 */
		this.Value = Value;

		/**
		 * Proportion of the value that's VAT
		 *
		 * @type {Number} 0-100
		 */
		this.VatProportion = VatProportion;

		/**
		 * VAT rate applied to the value
		 *
		 * @type {Number} 0-100
		 */
		this.VatRate = VatRate;

	}


	/**
	 * Get the value.
	 *
	 * @return {Integer} Pence, inc. VAT
	 */
	get() {
		return this.Value;
	}


	/**
	 * Get the portion of the value (pence) which is VAT.
	 *
	 * Uses the calculation method of `Hops\Type\Price` on the backend.
	 * 
	 * @return {Integer} Pence
	 */
	getVat() {
		return ((this.get() || 0) - this.getNonVat());
	}


	/**
	 * Get the portion of the value (pence) which is not VAT.
	 *
	 * Uses the calculation method of `Hops\Type\Price` on the backend.
	 *
	 * @return {Integer} Pence
	 */
	getNonVat() {
		const vatPropRate = (this.VatProportion * this.VatRate);
		const vatDivisor = ((10000 + vatPropRate) / 10000);
		return Math.round(((this.get() || 0) / vatDivisor));
	}


	/**
	 * Get the proportion of the value that's VAT.
	 *
	 * @return {Number}
	 */
	getVatProportion() {
		return this.VatProportion;
	}


	/**
	 * Get the VAT rate applied to the value.
	 *
	 * @return {Number}
	 */
	getVatRate() {
		return this.VatRate;
	}

}

export default Price;
